import React, { useEffect, useState, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useNotificationStore } from 'stores/notifications';
import { useNotificationCount } from 'features/notifications';
import { RecentNotifications } from './RecentNotifications';
import { useSocket } from 'providers/SocketProvider';
import { useTheme } from 'hooks/useTheme';

export const Notifications = () => {
  const notificationCount = useNotificationStore((s) => s.notificationCount);
  const setNotificationCount = useNotificationStore((s) => s.setNotificationCount);
  const { data, isSuccess } = useNotificationCount();
  const { socket } = useSocket();
  const { theme, background } = useTheme();

  const [hasNewNotification, setHasNewNotification] = useState(false);

  useEffect(() => {
    if (isSuccess && data?.notificationCount != null) {
      setNotificationCount(data.notificationCount);
    }
  }, [data, isSuccess, setNotificationCount]);

  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = () => {
      setNotificationCount((prev) => prev + 1);
      setHasNewNotification(true);

      const timeout = setTimeout(() => setHasNewNotification(false), 3000);
      return () => clearTimeout(timeout);
    };

    socket.onAny((eventName, payload) => {
      if (!payload?.data) return;
      handleNewNotification();
    });

    return () => {
      socket.offAny();
    };
  }, [socket, setNotificationCount]);

  return (
    <Menu as="div" className="ml-3 relative z-50">
      {({ open }) => (
        <>
          <Menu.Button>
            <div
              className={`relative p-2 mt-2 rounded-full bg-white/10 backdrop-blur-md transition-transform hover:scale-105 ${
                hasNewNotification ? 'animate-pulse ring-2 ring-red-500/80' : ''
              }`}
            >
              <Bell size={20} className="text-white" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
                  {notificationCount}
                </span>
              )}
            </div>
          </Menu.Button>

          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className={`origin-top-right absolute right-0 mt-2 w-80 rounded-xl p-4 max-h-[70vh] overflow-y-auto backdrop-blur-md shadow-xl ring-1 ring-white/10 focus:outline-none ${theme}`}
            >
              {/* Background image overlay */}
              {background && (
                <div className="absolute inset-0 pointer-events-none opacity-10 z-0">
                  <img
                    src={background}
                    alt="Background overlay"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="relative z-10">
                <RecentNotifications />

                <hr className="my-4 border-t border-white/10" />

                <div className="text-center">
                  <Link
                    to="/app/notifications"
                    className="text-sm underline text-blue-300 hover:text-blue-200"
                  >
                    Read more
                  </Link>
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};
